/**
 * GoalSelectionScreen (Screen 6)
 * 
 * "What are you preparing for?"
 * Refined for perfect vertical balance: maximum vertical lift and streamlined text.
 */
import React from 'react';
import { useOnboardingViewport } from '../shared/useViewport';
import { useOnboarding } from '../../../context/OnboardingContext';

const SelectionCard = ({ goal, selected, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      background: 'white',
      padding: '12px 20px',
      borderRadius: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      cursor: 'pointer',
      border: `2px solid ${selected ? goal.color : 'rgba(0,0,0,0.03)'}`,
      boxShadow: selected ? `0 10px 25px ${goal.color}10` : '0 2px 8px rgba(0,0,0,0.02)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      height: '84px'
    }}
  >
    {/* Icon Area */}
    <div style={{
      width: '56px',
      height: '56px',
      borderRadius: '16px',
      background: `${goal.color}10`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      {goal.icon}
    </div>

    {/* Text Area */}
    <div style={{ flex: 1, textAlign: 'left' }}>
      <div style={{ fontSize: '18px', fontWeight: '900', color: '#0F172A', lineHeight: '1.2' }}>{goal.title}</div>
    </div>

    {/* Checkbox */}
    <div style={{
      width: '28px',
      height: '28px',
      borderRadius: '10px',
      border: `2.5px solid ${selected ? goal.color : '#E2E8F0'}`,
      background: selected ? goal.color : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      flexShrink: 0
    }}>
      {selected && (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  </div>
);

export const GoalSelectionScreen = ({ onNext }) => {
  const vp = useOnboardingViewport();
  const { goals: savedGoals, updateOnboarding } = useOnboarding();
  const [selectedGoals, setSelectedGoals] = React.useState(savedGoals || []);

  const toggleGoal = (id) => {
    const newGoals = selectedGoals.includes(id) 
      ? selectedGoals.filter(g => g !== id) 
      : [...selectedGoals, id];
    
    setSelectedGoals(newGoals);
    updateOnboarding({ goals: newGoals });
  };

  const goals = [
    {
      id: 'gre',
      title: 'GRE',
      color: '#14B8A6',
      icon: (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
          <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
        </svg>
      )
    },
    {
      id: 'gmat',
      title: 'GMAT',
      color: '#8B5CF6',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      )
    },
    {
      id: 'general',
      title: 'General vocabulary improvement',
      color: '#F97316',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3-4-4-6.5c-1 2.5-2 4.9-4 6.5s-3 3.5-3 5.5a7 7 0 0 0 7 7z" />
        </svg>
      )
    }
  ];

  const primaryCTAStyle = {
    width: "100%",
    height: `${vp.spacing.ctaHeight}px`,
    background: selectedGoals.length > 0 ? "#14B8A6" : "#CBD5E1",
    borderRadius: `${vp.spacing.ctaBorderRadius}px`,
    border: "none",
    boxShadow: selectedGoals.length > 0 ? "0 8px 16px rgba(20, 184, 166, 0.2)" : "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "white",
    fontSize: `${vp.spacing.ctaFontSize}px`,
    fontWeight: "900",
    cursor: selectedGoals.length > 0 ? "pointer" : "default",
    transition: "all 0.3s ease",
  };

  const isDesktop = vp.isTablet;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: isDesktop ? '480px' : '100%',
      margin: isDesktop ? '0 auto' : undefined,
      animation: 'fadeIn 0.6s ease-out',
      position: 'relative',
      background: 'transparent'
    }}>
      {/* Top Icon Area — Maximum Lift */}
      <div style={{ display: 'flex', justifyContent: 'center', position: 'relative', marginTop: '-50px' }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#F0FDFA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 121, 130, 0.08)',
          position: 'relative'
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
          </svg>
          {/* Sparkles */}
          <div style={{ position: 'absolute', top: '-10px', left: '-20px', color: '#FDA4AF', opacity: 0.3, fontSize: '18px' }}>✦</div>
          <div style={{ position: 'absolute', top: '5px', right: '-25px', color: '#2DD4BF', opacity: 0.2, fontSize: '16px' }}>✦</div>
        </div>
      </div>

      {/* Headline & Subtext */}
      <div style={{ textAlign: 'center', marginTop: '15px' }}>
        <h2 style={{
          fontSize: '34px',
          fontWeight: '900',
          color: '#0F172A',
          lineHeight: '1.1',
          margin: '0 0 8px 0',
          letterSpacing: '-1.5px'
        }}>
          What are you<br />
          <span style={{ color: '#14B8A6' }}>preparing</span> for?
        </h2>
        <p style={{
          fontSize: '15px',
          color: '#64748B',
          lineHeight: '1.4',
          margin: '0 auto',
          maxWidth: '320px',
          fontWeight: '500'
        }}>
          Select all that apply. We'll personalize<br />
          your experience.
        </p>
      </div>

      {/* Selection Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '28px' }}>
        {goals.map(goal => (
          <SelectionCard
            key={goal.id}
            goal={goal}
            selected={selectedGoals.includes(goal.id)}
            onToggle={() => toggleGoal(goal.id)}
          />
        ))}
      </div>

      {/* Info Row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 16px',
        marginTop: '20px',
        opacity: 0.9
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '12px',
          background: '#F0FDFA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" />
          </svg>
        </div>
        <p style={{ fontSize: '13px', color: '#64748B', lineHeight: '1.4', margin: 0, textAlign: 'left', fontWeight: '500' }}>
          Your selections help us <span style={{ color: '#14B8A6', fontWeight: '700' }}>personalize</span> words for you.
        </p>
      </div>

      {/* CTA Button */}
      <div style={{
        paddingTop: '10px',
        zIndex: 10
      }}>
        <button
          onClick={onNext}
          disabled={selectedGoals.length === 0}
          style={primaryCTAStyle}
        >
          Continue
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default GoalSelectionScreen;
